import React, { useEffect, useState } from 'react';
import { Stack, Spinner } from '@guestyci/arc';

import CustomLoginFlow from './CustomLoginFlow';
import CustomRegistrationFlow from './CustomRegistrationFlow';
import { CustomResetPassword } from './CustomResetPassword';
import CustomMFA from './CustomMFA';
import CustomEmailMFA from './CustomEmailMFA';
import CustomPhoneMFA from './CustomPhoneMFA/CustomPhoneMFA';
import { useOktaWidget } from '../hooks/useOktaWidget';
import { WIDGET_STATES } from '../config/widgetStates';
import CustomGoogleMFA from './CustomGoogleMFA/CustomGoogleMFA';
import CustomPasswordExpired from './CustomPasswordExpired';
import './OktaWidget.css';

const HybridOktaLogin = ({ onViewStateChange, onTokenExpired, onWelcomeStepChange }) => {
  const {
    containerRef,
    isWidgetRendered,
    error,
    isSubmitting,
    showCustomUI,
    oktaViewState,
    mfaConfig,
    totpData,
    userEmail,
    loginPrefill,
    passwordExpiryInfo,
    submitCredentials,
    changeExpiredPassword,
    signOutFromPasswordExpired,
    remindLaterPasswordExpired,
    createPassword,
    triggerGoogleSignIn,
    advanceTotpEnrollment,
    revealManualSetupKey,
    submitTotpCode,
    navigateBackToEnrollChoices,
    submitMfaVerificationCode,
    resendMfaCode,
    clearError,
    hasGoogleIDP,
    isTokenExpired,
    smsCodeSent,
    smsPhoneNumber,
    sendSmsCode,
    resendSmsCode,
    submitSmsVerificationCode,
  } = useOktaWidget();

  useEffect(() => {
    onTokenExpired?.(isTokenExpired);
  }, [isTokenExpired, onTokenExpired]);

  const widgetHidden = !isWidgetRendered || showCustomUI;

  const isOnboarding = sessionStorage.getItem('isOnboardingFlow');
  const [registrationStep, setRegistrationStep] = useState(null);

  useEffect(() => {
    if (onViewStateChange) {
      onViewStateChange(oktaViewState, registrationStep);
    }
  }, [oktaViewState, onViewStateChange, registrationStep]);

  const renderCustomUI = () => {
    switch (oktaViewState) {
      case WIDGET_STATES.PRIMARY_AUTH:
        return (
          <CustomLoginFlow
            onSubmit={submitCredentials}
            onGoogleSignIn={triggerGoogleSignIn}
            error={error}
            isSubmitting={isSubmitting}
            onClearError={clearError}
            showGoogleLogin={hasGoogleIDP}
            isWidgetRendered={isWidgetRendered}
            loginPrefill={loginPrefill}
          />
        );
      case WIDGET_STATES.PASSWORD_EXPIRED:
        return (
          <CustomPasswordExpired
            passwordExpiryInfo={passwordExpiryInfo}
            onChangePassword={changeExpiredPassword}
            onSignOut={signOutFromPasswordExpired}
            onRemindLater={remindLaterPasswordExpired}
            error={error}
            isSubmitting={isSubmitting}
          />
        );
      case WIDGET_STATES.PASSWORD_RESET:
        return isOnboarding ? (
          <CustomRegistrationFlow
            onCreatePassword={createPassword}
            error={error}
            isSubmitting={isSubmitting}
            onClearError={clearError}
            onStepChange={setRegistrationStep}
            onWelcomeStepChange={onWelcomeStepChange}
          />
        ) : (
          <CustomResetPassword
            onCreatePassword={createPassword}
            error={error}
            isSubmitting={isSubmitting}
          />
        );
      case WIDGET_STATES.ENROLL_CHOICES:
        return <CustomMFA mfaConfig={mfaConfig} />;
      case WIDGET_STATES.ENROLL_TOTP:
      case WIDGET_STATES.BARCODE_TOTP:
      case WIDGET_STATES.ACTIVATE_TOTP:
      case WIDGET_STATES.ENROLL_MANUAL_TOTP:
      case WIDGET_STATES.MFA_VERIFY_GOOGLE_AUTH:
        return (
          <CustomGoogleMFA
            oktaViewState={oktaViewState}
            isVerifyMode={oktaViewState === WIDGET_STATES.MFA_VERIFY_GOOGLE_AUTH}
            qrCodeSrc={totpData?.qrCodeSrc}
            manualSetupKey={totpData?.manualSetupKey}
            onAdvanceEnrollment={advanceTotpEnrollment}
            onRevealManualKey={revealManualSetupKey}
            onVerifyCode={submitTotpCode}
            onPickDifferentMethod={navigateBackToEnrollChoices}
            error={error}
            isSubmitting={isSubmitting}
          />
        );
      case WIDGET_STATES.MFA_ENROLL_EMAIL:
      case WIDGET_STATES.MFA_ENROLL_ACTIVATE_EMAIL:
      case WIDGET_STATES.MFA_VERIFY_EMAIL:
        return (
          <CustomEmailMFA
            userEmail={userEmail}
            isVerifyMode={oktaViewState === WIDGET_STATES.MFA_VERIFY_EMAIL}
            onSubmitCode={submitMfaVerificationCode}
            onResendCode={resendMfaCode}
            onPickDifferentMethod={navigateBackToEnrollChoices}
            error={error}
            isSubmitting={isSubmitting}
          />
        );
      case WIDGET_STATES.MFA_ENROLL_SMS:
      case WIDGET_STATES.MFA_VERIFY_SMS:
        return (
          <CustomPhoneMFA
            smsCodeSent={smsCodeSent}
            smsPhoneNumber={smsPhoneNumber}
            isVerifyMode={oktaViewState === WIDGET_STATES.MFA_VERIFY_SMS}
            onSendCode={sendSmsCode}
            onResendCode={resendSmsCode}
            onSubmitCode={submitSmsVerificationCode}
            onPickDifferentMethod={navigateBackToEnrollChoices}
            error={error}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Stack spacing={6} className="gst-w-full">
      {isWidgetRendered
        ? renderCustomUI()
        : (
          <Stack align="center" justify="center" className="gst-py-8">
            <Spinner size="xl" />
          </Stack>
        )}

      <div
        ref={containerRef}
        className="new-auth-widget gst-w-full"
        style={{ display: widgetHidden ? 'none' : 'block' }}
      />
    </Stack>
  );
};

export default HybridOktaLogin;
